import { faker } from '@faker-js/faker';
import { formatPhoneNumber } from './phoneFormatter';
import { generatePatientData } from './dataGenerators';
import { generateConstraints, generateAppointments } from './constraintBuilders';
import { buildCaseSpecificData } from './caseDataBuilders';

/**
 * Builds the standard request object for case creation
 * @param {object} options - Build options
 * @returns {object} Request object for API
 */
export const buildStandardRequestObject = ({
  formData,
  planId,
  planType,
  aptType,
  cdtCodes,
  providerId,
}) => {
  const fakeData = generatePatientData();
  const isMinimalOrCustom = formData.ctype === 'Custom - All fields' || formData.ctype === 'Minimal - Select fields';
  const formattedPhoneNumber = formatPhoneNumber(formData.phoneNumber);

  const constraints = generateConstraints();
  const appointments = generateAppointments({
    aptType,
    planName: formData.plan,
    providerId,
  });

  const firstName = isMinimalOrCustom ? formData.firstName : fakeData.firstName;
  const lastName = isMinimalOrCustom ? formData.lastName : fakeData.lastName;

  return {
    name: formData.plan + " Test",
    outcomePlanId: {
      id: planId,
      versionNumber: 1
    },
    cdtCodes: cdtCodes || [],
    providerId,
    patient: {
      firstName,
      lastName,
      dob: isMinimalOrCustom ? formData.dob : fakeData.dob,
      gender: isMinimalOrCustom ? formData.gender : fakeData.gender,
      phoneNumber: formattedPhoneNumber,
      postalCode: isMinimalOrCustom ? formData.postalCode : fakeData.postalCode,
      mobilePhoneNumber: formattedPhoneNumber,
      mrn: fakeData.mrn,
      umrn: fakeData.umrn,
      source: process.env.REACT_APP_FORM_SOURCE,
      pronoun: fakeData.pronoun,
      conversationStyle: fakeData.conversationStyle,
      communicationChannel: formData.channel,
      constraintList: constraints,
      ...(formData.patientExternalId && { externalId: formData.patientExternalId }),
    },
    state: "ACTIVE",
    communicationChannel: formData.channel,
    searchText: formData.plan + ' ' + firstName + ' ' + lastName,
    appointments,
    medications: []
  };
};

/**
 * Builds request object for plans with custom caseSpecificData fields
 * @param {object} options - Build options
 * @param {object} options.formData - Form data
 * @param {string} options.planId - Plan ID
 * @param {string} options.providerId - Provider ID
 * @param {object} options.customFieldValues - Custom field values
 * @param {object} [options.orgContext={}] - Organization context for config lookup
 * @returns {object} Request object for API
 */
export const buildCustomFieldsRequestObject = ({
  formData,
  planId,
  providerId,
  customFieldValues,
  orgContext = {},
}) => {
  const fakeData = generatePatientData();
  const isMinimal = formData.ctype === 'Minimal - Select fields';
  const formattedPhoneNumber = formatPhoneNumber(formData.phoneNumber);

  return {
    name: formData.plan + " Test",
    outcomePlanId: {
      id: planId,
      versionNumber: 1
    },
    patient: {
      patientId: fakeData.patientId,
      firstName: isMinimal ? formData.firstName : fakeData.firstName,
      lastName: isMinimal ? formData.lastName : fakeData.lastName,
      dob: isMinimal ? formData.dob : fakeData.dob,
      gender: isMinimal ? formData.gender : fakeData.gender,
      pronoun: fakeData.pronoun,
      mobilePhoneNumber: formattedPhoneNumber,
      postalCode: fakeData.postalCode,
      externalId: formData.patientExternalId || fakeData.externalId,
    },
    state: "ACTIVE",
    communicationChannel: formData.channel,
    providerId,
    appointments: [
      {
        title: "Annual",
        type: "PCP_FOLLOW_UP",
        date: null,
        providerId: null
      }
    ],
    preferredLanguage: "en-US",
    caseSpecificData: buildCaseSpecificData(formData.plan, customFieldValues, orgContext),
    assistantGeneratedData: {}
  };
};

/**
 * Adjusts appointment date for specific plans
 * @param {object} requestObject - The request object to modify
 * @param {string} planName - Name of the plan
 * @returns {object} Modified request object
 */
export const adjustAppointmentDateForPlan = (requestObject, planName) => {
  if (planName === 'MIMIT Appointment Reminder' || planName === 'Outreach-Appointment-Rescheduling_RW') {
    const adate = new Date();
    adate.setDate(adate.getDate() + 10);
    requestObject.appointments[0].date = adate.toISOString();
  }
  return requestObject;
};
